import type { FlightState } from './geo';

/** cubic-bezier control points [x1, y1, x2, y2], as CSS easing */
export type BezierPoints = [number, number, number, number];

export interface PostState {
  lensFlare: boolean;
  lensFlareIntensity: number;
  flareGhosts: boolean;
  flareGhostSize: number;
  flareGlareSize: number;
  flareStarPoints: number;
  flareAnamorphic: boolean;
  pixelation: boolean;
  noiseOpacity: number;
  bloomIntensity: number;
  bloomThreshold: number;
  bloomSmoothing: number;
  scanlineDensity: number;
  scanlineOpacity: number;
  saturation: number;
  brightness: number;
  contrast: number;
  vignetteOffset: number;
  vignetteDarkness: number;
  fisheyeStrength: number;
  fisheyeZoom: number;
}

export const POST_DEFAULTS: PostState = {
  lensFlare: true,
  lensFlareIntensity: 0.35,
  flareGhosts: true,
  flareGhostSize: 0.2,
  flareGlareSize: 0.12,
  flareStarPoints: 6,
  flareAnamorphic: false,
  pixelation: false,
  noiseOpacity: 0.13,
  bloomIntensity: 0.0025,
  bloomThreshold: 0.27,
  bloomSmoothing: 0.34,
  scanlineDensity: 2,
  scanlineOpacity: 0.2,
  saturation: -1,
  brightness: -0.4,
  contrast: 0,
  vignetteOffset: 0,
  vignetteDarkness: 1,
  fisheyeStrength: 0.075,
  fisheyeZoom: 1,
};

export interface StarsState {
  intensity: number;
  forceNight: boolean;
}

export const STARS_DEFAULTS: StarsState = {
  intensity: 60,
  forceNight: false,
};

export interface CameraState {
  /** pitch in degrees at the top of the page (90 = straight up) */
  pitchTop: number;
  pitchBottom: number;
  fovTop: number;
  fovBottom: number;
  /** camera altitude in metres — raise the bottom value to fly over the deck */
  heightTop: number;
  heightBottom: number;
  sway: number;
  swayBottom: number;
  darkenOnScroll: number;
  /** scroll→pitch easing curve — back-load it (ease-in) so the horizon reveal
      comes late and fast, the "breaking out of the cloud deck" beat */
  pitchBezier: BezierPoints;
  /** scroll→climb easing curve — front-load it (ease-out) to clear the deck
      early, so you're above the clouds before the horizon swings in */
  heightBezier: BezierPoints;
  fovBezier: BezierPoints;
  pitchFollow: number;
  followSpeed: number;
}

export const CAMERA_DEFAULTS: CameraState = {
  pitchTop: 90,
  pitchBottom: 0,
  fovTop: 60,
  fovBottom: 30,
  heightTop: 32,
  heightBottom: 1600,
  sway: 1,
  swayBottom: 0.05,
  darkenOnScroll: 0.3,
  pitchBezier: [0.65, 0, 0.36, 1],
  heightBezier: [0.42, 0, 1, 1],
  fovBezier: [0.65, 0.05, 0.36, 1],
  pitchFollow: 3,
  followSpeed: 2,
};

export interface IntroState {
  fadeMs: number;
  /** seconds after the scene settles before the clouds start building */
  cloudsDelay: number;
  cloudsDuration: number;
}

export const INTRO_DEFAULTS: IntroState = {
  fadeMs: 1400,
  cloudsDelay: 0.4,
  cloudsDuration: 2.6,
};

export interface TrafficState {
  trailOpacity: number;
  trailSeconds: number;
  dotSize: number;
  reticleSize: number;
  reticleOnHover: boolean;
}

export const TRAFFIC_DEFAULTS: TrafficState = {
  trailOpacity: 0.1,
  trailSeconds: 160,
  dotSize: 1.5,
  reticleSize: 16,
  reticleOnHover: true,
};

export interface CloudInteractionState {
  minRadius: number;
  radiusEasing: number;
  edgeSoftness: number;
  trailDuration: number;
  fullStrengthSpeed: number;
  enabled: boolean;
  radius: number;
  strength: number;
}

export const CLOUD_INTERACTION_DEFAULTS: CloudInteractionState = {
  minRadius: 128,
  radiusEasing: 0.18,
  edgeSoftness: 1,
  trailDuration: 1,
  fullStrengthSpeed: 600,
  enabled: true,
  radius: 256,
  strength: 0.25,
};

export interface CloudLightingState {
  minIrradiance: number;
  skyLightScale: number;
  powderScale: number;
  powderExponent: number;
}

export const CLOUD_LIGHTING_DEFAULTS: CloudLightingState = {
  minIrradiance: 0.5,
  skyLightScale: 0,
  powderScale: 1,
  powderExponent: 200,
};

export interface WaterState {
  speed: number;
  rippleSize: number;
  distortion: number;
  roughness: number;
  color: string;
  sunlight: number;
}

export const WATER_DEFAULTS: WaterState = {
  speed: 0.55,
  rippleSize: 1,
  distortion: 3.2,
  roughness: 0.35,
  color: '#001e2f',
  sunlight: 1,
};

export type SkySceneMode = 'sky' | 'water';

export interface SkyState {
  hour: number | null;
  coverage: number;
  scroll: number;
  darken: number;
  cloudsClear: boolean;
  flights: FlightState[];
  trafficReveal: number;
  /** view heading offset in degrees, dragged on the compass */
  yaw: number;
  traffic: TrafficState;
  hoveredFlight: string | null;
  post: PostState;
  camera: CameraState;
  intro: IntroState;
  postEnabled: boolean;
  cloudsEnabled: boolean;
  cloudsQuality: 'low' | 'medium';
  cloudLighting: CloudLightingState;
  cloudInteraction: CloudInteractionState;
  stars: StarsState;
  sceneMode: SkySceneMode;
  water: WaterState;
}

export const skyState: SkyState = {
  hour: null,
  coverage: 0.5,
  scroll: 0,
  darken: 0,
  cloudsClear: false,
  flights: [],
  trafficReveal: 0,
  yaw: 0,
  traffic: { ...TRAFFIC_DEFAULTS },
  hoveredFlight: null,
  post: { ...POST_DEFAULTS },
  camera: { ...CAMERA_DEFAULTS },
  intro: { ...INTRO_DEFAULTS },
  postEnabled: true,
  cloudsEnabled: true,
  cloudsQuality: 'medium',
  cloudLighting: { ...CLOUD_LIGHTING_DEFAULTS },
  cloudInteraction: { ...CLOUD_INTERACTION_DEFAULTS },
  stars: { ...STARS_DEFAULTS },
  sceneMode: 'sky',
  water: { ...WATER_DEFAULTS },
};

type Listener = () => void;
const listeners = new Set<Listener>();

export const subscribeSky = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const updateSky = (partial: Partial<SkyState>): void => {
  Object.assign(skyState, partial);
  for (const listener of listeners) listener();
};
